// src/utils/sessionOverview.js
//
// Session-level rollup for the top of the Slots view (docs/slot-story-spec.md,
// "Session overview"). Pure: lifecycles in, chart-ready data out. Every number
// is derived from the per-slot story (utils/slotStory.js), so the overview can
// never disagree with the slot rows below it. Unknown (USB) stays unknown: it
// is excluded from rates, never counted as a miss.
import { buildSlotStory } from './slotStory';
import { platformOf } from '../validation/bidHealth';

// Same-lane requests closer than this are one burst. GAM's minimum refresh is
// 30 s, so several requests for one unit inside 5 s are duplicates, not refresh.
export const BURST_GAP_MS = 5000;

const toMs = (v) => (typeof v === 'number' ? v : Date.parse(v) || 0);

/** `/4145/FNC/droidmob/hp/adh` -> `hp/adh` (enough to tell hp/adh from pol/adh). */
export function unitLabel(adUnit) {
  return String(adUnit || '?').split('/').filter(Boolean).slice(-2).join('/') || '?';
}

/** Winner bucket for the "who won" chart. */
export function winnerBucket(story) {
  const f = story.fill && story.fill.state;
  if (f === 'nofill') return 'nofill';
  if (story.winner) {
    if (story.winner.kind === 'mediation') return 'sdk';
    if (story.winner.kind === 'prebid') return 'prebid';
    return 'gam';
  }
  if (f === 'filled') return 'filled';
  return 'unknown';
}

export const WINNER_BUCKETS = ['sdk', 'prebid', 'gam', 'filled', 'nofill', 'unknown'];

function platformOfLc(lc) {
  const row = (lc && lc.phases && lc.phases.gam && lc.phases.gam.row) || {};
  const p = platformOf({ ...row, validData: { ...(row.validData || {}), adUnit: (row.validData && row.validData.adUnit) || lc.adUnit } });
  // All-devices scope tags each lifecycle with its device tab; use it when the
  // row itself can't say (e.g. two Androids, or a row with no platform fields).
  return p === 'Other' && lc.device ? lc.device : p;
}

function hasAmznbid(lc) {
  const cp = (lc && lc.custParams) || {};
  const a = lc && lc.phases && lc.phases.amazon;
  return !!((a && (a.amznB || a.passedToGAM)) || cp.amznbid || cp.amzn_b || cp.amznb);
}

/**
 * @param {object[]} lifecycles  server/local slot lifecycles
 * @returns {{
 *   start: number, end: number,
 *   platforms: object[],   // one card per platform seen
 *   lanes: object[],       // timeline: { platform, unit, marks[] }, marks grouped into bursts
 *   wins: object[],        // { platform, unit, counts: {bucket: n}, total }
 * }}
 */
export function buildSessionOverview(lifecycles) {
  const items = (Array.isArray(lifecycles) ? lifecycles : [])
    .filter(Boolean)
    .map((lc) => {
      const story = buildSlotStory(lc);
      return { lc, story, t: toMs(lc.timestamp), platform: platformOfLc(lc), unit: unitLabel(lc.adUnit), bucket: winnerBucket(story) };
    })
    .filter((x) => x.t > 0)
    .sort((a, b) => a.t - b.t);

  const start = items.length ? items[0].t : 0;
  const end = items.length ? items[items.length - 1].t : 0;

  // ── lanes + bursts
  const laneMap = new Map();
  for (const it of items) {
    const k = `${it.platform}\u0001${it.unit}`;
    if (!laneMap.has(k)) laneMap.set(k, { platform: it.platform, unit: it.unit, marks: [] });
    const lane = laneMap.get(k);
    const last = lane.marks[lane.marks.length - 1];
    if (last && it.t - last.last <= BURST_GAP_MS) {
      last.items.push(it);
      last.last = it.t;
    } else {
      lane.marks.push({ t: it.t, last: it.t, items: [it] });
    }
  }
  const lanes = Array.from(laneMap.values()).map((lane) => ({
    ...lane,
    marks: lane.marks.map((m) => ({
      t: m.t,
      last: m.last,
      count: m.items.length,
      burst: m.items.length > 1,
      keys: m.items.map((x) => x.story.key),
      fill: m.items.length === 1 ? m.items[0].story.fill.state : null,
      winners: m.items.map((x) => (x.story.winner ? x.story.winner.name : null)),
      filledCount: m.items.filter((x) => x.story.fill.state === 'filled').length,
      hbPb: m.items.length === 1 ? ((x) => (x.lc.phases && x.lc.phases.gam && x.lc.phases.gam.hbPb) || (x.lc.custParams && x.lc.custParams.hb_pb) || null)(m.items[0]) : null,
    })),
  }));
  lanes.sort((a, b) => a.platform.localeCompare(b.platform) || a.unit.localeCompare(b.unit));

  // ── who won
  const winMap = new Map();
  for (const it of items) {
    const k = `${it.platform}\u0001${it.unit}`;
    if (!winMap.has(k)) winMap.set(k, { platform: it.platform, unit: it.unit, counts: Object.fromEntries(WINNER_BUCKETS.map((b) => [b, 0])), total: 0 });
    const w = winMap.get(k);
    w.counts[it.bucket] += 1;
    w.total += 1;
  }
  const wins = Array.from(winMap.values()).sort((a, b) => a.unit.localeCompare(b.unit) || a.platform.localeCompare(b.platform));

  // ── platform cards
  const byPlat = new Map();
  for (const it of items) {
    if (!byPlat.has(it.platform)) byPlat.set(it.platform, []);
    byPlat.get(it.platform).push(it);
  }
  const platforms = Array.from(byPlat.entries()).map(([platform, list]) => {
    const usb = list.filter((x) => x.story.usb).length;
    const known = list.filter((x) => x.story.fill.state === 'filled' || x.story.fill.state === 'nofill');
    const filled = known.filter((x) => x.story.fill.state === 'filled').length;
    const prebidSent = list.filter((x) => (x.lc.phases && x.lc.phases.gam && x.lc.phases.gam.hbPb) || (x.lc.custParams && x.lc.custParams.hb_pb)).length;
    // APS targeting sits past logcat's ~4 KB cut, so only MITM slots can answer
    const mitm = list.filter((x) => !x.story.usb);
    const apsSent = mitm.filter((x) => hasAmznbid(x.lc)).length;
    const wonKnown = list.filter((x) => x.story.winner);
    const sdkWins = wonKnown.filter((x) => x.story.winner.kind === 'mediation').length;
    const bursts = lanes.filter((l) => l.platform === platform).reduce((n, l) => n + l.marks.filter((m) => m.burst).length, 0);
    return {
      platform,
      capture: usb === 0 ? 'MITM' : usb === list.length ? 'USB' : 'USB + MITM',
      slots: list.length,
      fillKnown: known.length,
      filled,
      fillRate: known.length ? filled / known.length : null,
      prebidSent,
      apsSent: mitm.length ? apsSent : null,
      apsOf: mitm.length,
      winsKnown: wonKnown.length,
      sdkWins,
      bursts,
    };
  }).sort((a, b) => a.platform.localeCompare(b.platform));

  // ── flat per-slot facts (funnel + findings read these)
  const facts = items.map((it) => ({
    key: it.story.key,
    t: it.t,
    platform: it.platform,
    unit: it.unit,
    family: unitFamily(it.unit),
    usb: it.story.usb,
    hbPb: !!((it.lc.phases && it.lc.phases.gam && it.lc.phases.gam.hbPb) || (it.lc.custParams && it.lc.custParams.hb_pb)),
    amzn: hasAmznbid(it.lc),
    fill: it.story.fill.state,
    winnerKind: it.story.winner ? it.story.winner.kind : null,
    winnerName: it.story.winner ? it.story.winner.name : null,
    imp: !!(it.lc.phases && it.lc.phases.impression && it.lc.phases.impression.fired),
  }));

  return { start, end, platforms, lanes, wins, facts };
}

/** `hp/adh` -> `adh`, `art/ban2` -> `ban`: the slot's role, across pages. */
export function unitFamily(unit) {
  const last = String(unit || '').split('/').pop() || '?';
  return last.replace(/\d+$/, '') || last;
}
const FAMILY_LABEL = { adh: 'Anchor', ban: 'Banners', nat: 'Native', native: 'Native', int: 'Interstitial', inter: 'Interstitial', vid: 'Video', rew: 'Rewarded' };
export const familyLabel = (f) => (FAMILY_LABEL[f] ? `${FAMILY_LABEL[f]} (${f})` : f);

/**
 * Funnel columns (platform × unit family, top 3 families per platform) and
 * stages. APS counts only non-USB slots (logcat cuts amznbid); a column with no
 * MITM slot shows n/a there, never 0.
 */
export function buildFunnel(facts) {
  const plats = [...new Set((facts || []).map((f) => f.platform))].sort();
  const multi = plats.length > 1;
  const cols = [];
  for (const p of plats) {
    const mine = facts.filter((f) => f.platform === p);
    const fams = Object.entries(mine.reduce((m, f) => { m[f.family] = (m[f.family] || 0) + 1; return m; }, {}))
      .sort((a, b) => b[1] - a[1]).slice(0, 3).map(([f]) => f);
    for (const fam of fams) cols.push({ platform: p, family: fam, label: `${multi ? `${p} · ` : ''}${familyLabel(fam)}`, rows: mine.filter((f) => f.family === fam) });
  }
  const stage = (id, label, test, onlyMitm) => ({
    id, label,
    cells: cols.map((c) => {
      const pool = onlyMitm ? c.rows.filter((r) => !r.usb) : c.rows;
      if (!pool.length) return { n: null, of: 0 };
      return { n: pool.filter(test).length, of: pool.length };
    }),
  });
  const stages = [
    stage('requests', 'Requests', () => true),
    stage('hb', 'Prebid price sent (hb_pb)', (r) => r.hbPb),
    stage('aps', 'APS bid sent (amznbid)', (r) => r.amzn, true),
    stage('winner', 'Winner known', (r) => r.fill === 'filled' && !!r.winnerKind),
    stage('imp', 'Impression fired', (r) => r.imp),
  ];
  return { cols: cols.map(({ rows, ...c }) => ({ ...c, n: rows.length })), stages };
}

/**
 * Plain-language findings, most actionable first. Each carries the slot keys it
 * is about so the UI can open them. Conservative: a finding needs enough
 * requests to mean something, and never treats USB-invisible data as missing.
 */
export function buildFindings(overview) {
  const { facts = [], lanes = [], platforms = [] } = overview || {};
  const out = [];
  const fmt = (t) => { const d = new Date(t); const p = (n) => String(n).padStart(2, '0'); return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`; };
  const multi = platforms.length > 1;
  const on = (p) => (multi ? ` on ${p}` : '');

  // 1. Prebid price missing on a unit family (and present elsewhere)
  const famKeys = [...new Set(facts.map((f) => `${f.platform}\u0001${f.family}`))];
  for (const k of famKeys) {
    const [p, fam] = k.split('\u0001');
    const rows = facts.filter((f) => f.platform === p && f.family === fam);
    if (rows.length < 3 || rows.some((r) => r.hbPb)) continue;
    const elsewhereSame = facts.filter((f) => f.family === fam && f.platform !== p);
    const otherFam = facts.filter((f) => f.platform === p && f.family !== fam);
    const ref = elsewhereSame.length && elsewhereSame.some((r) => r.hbPb)
      ? `, while ${elsewhereSame[0].platform} sends one on ${elsewhereSame.filter((r) => r.hbPb).length}/${elsewhereSame.length} — the gap is ${p}-specific`
      : otherFam.some((r) => r.hbPb) ? `, while other units${on(p)} do (${otherFam.filter((r) => r.hbPb).length}/${otherFam.length})` : null;
    if (!ref) continue;
    out.push({ kind: 'problem', text: `No ${familyLabel(fam)} request${on(p)} carried a Prebid price to GAM (0/${rows.length})${ref}. Check that this unit and its sizes are in the Prebid ad-unit config.`, keys: rows.map((r) => r.key) });
  }

  // 2. Duplicate bursts
  for (const l of lanes) {
    for (const m of l.marks.filter((x) => x.burst)) {
      out.push({ kind: 'problem', text: `${l.unit}${on(l.platform)} requested ${m.count}× in ${((m.last - m.t) / 1000).toFixed(1)} s at ${fmt(m.t)}: likely duplicate requests (GAM refresh is ≥30 s). Duplicates waste impressions and can look like IVT.`, keys: m.keys });
    }
  }

  // 3. No fill concentrated on a unit
  const units = [...new Set(facts.map((f) => `${f.platform}\u0001${f.unit}`))];
  for (const k of units) {
    const [p, u] = k.split('\u0001');
    const known = facts.filter((f) => f.platform === p && f.unit === u && (f.fill === 'filled' || f.fill === 'nofill'));
    const nf = known.filter((f) => f.fill === 'nofill');
    if (nf.length >= 2 && nf.length / known.length >= 0.5) {
      out.push({ kind: 'problem', text: `${u}${on(p)} did not fill ${nf.length} of ${known.length} times. Open one to see the "Why no fill" walk-through.`, keys: nf.map((f) => f.key) });
    }
  }

  // 4. SDK bidding dominance where Prebid is thin
  for (const pc of platforms) {
    if (pc.winsKnown < 3 || pc.sdkWins / pc.winsKnown < 0.6) continue;
    const rows = facts.filter((f) => f.platform === pc.platform);
    const hbShare = rows.length ? rows.filter((r) => r.hbPb).length / rows.length : 0;
    if (hbShare >= 0.5) continue;
    const name = (facts.find((f) => f.platform === pc.platform && f.winnerKind === 'mediation') || {}).winnerName || 'SDK bidding';
    out.push({ kind: 'opportunity', text: `${name} wins ${pc.sdkWins} of ${pc.winsKnown} known outcomes${on(pc.platform)} while only ${Math.round(hbShare * 100)}% of requests carry a Prebid price. More header-bidding competition should raise prices here.`, keys: rows.filter((r) => r.winnerKind === 'mediation').map((r) => r.key) });
  }

  // 5. Caveat: what USB could not show
  for (const pc of platforms) {
    const rows = facts.filter((f) => f.platform === pc.platform);
    const usb = rows.filter((r) => r.usb);
    const hidden = usb.filter((r) => r.fill === 'unknown').length;
    if (!usb.length) continue;
    out.push({ kind: 'caveat', text: `${multi ? `${pc.platform}: ` : ''}${usb.length} slot${usb.length === 1 ? ' was' : 's were'} captured over USB, so APS targeting and ${hidden} outcome${hidden === 1 ? '' : 's'} are not visible. One MITM capture of the same pages fills these in.`, keys: [] });
  }

  return out;
}
